import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  package_list,
  package_remove,
  package_save,
  student_package_list,
} from "../../../apis/Course";

// Course packages — the "Special Offer" bundles from our brochures.
//
// Offers change every term, and two offers on the same brochure can differ
// only by a condition (Offer 1 requires WRMT alongside and costs $150 less
// than Offer 2, which does not). Rather than model that, staff create each
// offer as its own product: a price, a sale window, and the sessions it
// covers.
//
// The package price covers ONLY the listed sessions. A condition such as
// "must also enrol in WRMT" is recorded as a condition — the WRMT fee is
// charged separately through the normal per-lesson fee schedule.

const BRAND = "#A6192E";

const s = {
  intro: { fontSize: 13, color: "#6b7280", margin: "0 0 18px", lineHeight: 1.7, maxWidth: 820 },
  panel: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "18px 18px 20px", marginBottom: 18,
  },
  panelTitle: { fontSize: 14, fontWeight: 700, color: "#1f2937", margin: "0 0 14px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.4px",
    textTransform: "uppercase", color: "#6b7280",
    borderBottom: "1px solid #e5e7eb", padding: "8px 10px",
  },
  td: { padding: "10px", borderBottom: "1px solid #f3f4f6", color: "#374151", verticalAlign: "top" },
  money: { fontWeight: 700, color: "#111827", whiteSpace: "nowrap" },
  save: { color: "#047857", fontWeight: 600, fontSize: 12 },
  pill: { display: "inline-block", fontSize: 10, fontWeight: 700, borderRadius: 999, padding: "2px 9px" },
  on: { background: "#dcfce7", color: "#166534" },
  off: { background: "#f3f4f6", color: "#6b7280" },
  expired: { background: "#fef2f2", color: "#991b1b" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#4b5563" },
  hint: { fontSize: 11, color: "#9ca3af" },
  input: {
    height: 38, borderRadius: 6, border: "1px solid #d1d5db",
    padding: "0 10px", fontSize: 14, width: "100%",
  },
  textarea: {
    minHeight: 70, borderRadius: 6, border: "1px solid #d1d5db",
    padding: "8px 10px", fontSize: 13, width: "100%", fontFamily: "inherit",
  },
  btn: {
    background: BRAND, color: "#fff", border: "none", borderRadius: 6,
    padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  btnGhost: {
    background: "#fff", color: "#374151", border: "1px solid #d1d5db",
    borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  btnLink: { background: "none", border: "none", color: BRAND, fontSize: 12, cursor: "pointer", padding: 0 },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1.2fr 70px 100px 90px 30px",
    gap: 8, marginBottom: 8, alignItems: "center",
  },
  itemHead: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1.2fr 70px 100px 90px 30px",
    gap: 8, marginBottom: 6,
    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    color: "#9ca3af", letterSpacing: "0.4px",
  },
  small: { height: 34, borderRadius: 6, border: "1px solid #d1d5db", padding: "0 8px", fontSize: 13, width: "100%" },
  totals: {
    background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "12px 14px", marginTop: 10, fontSize: 13, color: "#374151", lineHeight: 1.8,
  },
  note: {
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
    padding: "12px 14px", fontSize: 12.5, lineHeight: 1.7, color: "#78350f", marginTop: 14,
  },
  empty: {
    border: "1px dashed #d1d5db", borderRadius: 8, padding: "20px",
    textAlign: "center", fontSize: 13, color: "#6b7280",
  },
};

const blankItem = () => ({
  term_label: "", course_name: "", weeks: "", course_fee: "", book_fee: "",
});

const blank = {
  id: null, name: "", code: "", summary: "", price: "", normal_price: "",
  book_fee: "", sale_ends_on: "", requires_note: "", conditions: "", status: 1,
  items: [blankItem()],
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Packages = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [members, setMembers] = useState([]);

  const load = async () => {
    const res = await package_list(true);
    setRows(Array.isArray(res?.data) ? res.data : []);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setItem = (i, k) => (e) =>
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: e.target.value };
      return { ...f, items };
    });

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const dropItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }));

  // Adding up the rows is the point of the table — staff should not have to
  // total the brochure by hand to check they typed it in correctly.
  const itemTotal = form.items.reduce((a, it) => a + Number(it.course_fee || 0), 0);
  const bookTotal = form.items.reduce((a, it) => a + Number(it.book_fee || 0), 0);
  const saving_ = Number(form.normal_price || 0) - Number(form.price || 0);

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error("Name and code are both required");
      return;
    }
    if (form.price === "" || Number(form.price) <= 0) {
      toast.error("Enter the package price");
      return;
    }
    setSaving(true);
    const res = await package_save({
      ...form,
      price: Number(form.price),
      normal_price: Number(form.normal_price || itemTotal),
      book_fee: Number(form.book_fee || bookTotal),
      items: form.items.filter((it) => it.term_label || it.course_name),
    });
    setSaving(false);
    if (res?.status) {
      setForm(blank);
      load();
    }
  };

  const edit = (p) =>
    setForm({
      id: p.id, name: p.name, code: p.code, summary: p.summary || "",
      price: p.price, normal_price: p.normal_price, book_fee: p.book_fee,
      sale_ends_on: p.sale_ends_on ? String(p.sale_ends_on).slice(0, 10) : "",
      requires_note: p.requires_note || "", conditions: p.conditions || "",
      status: p.status,
      items: p.items?.length ? p.items.map((i) => ({ ...i })) : [blankItem()],
    });

  const remove = async (id) => {
    const res = await package_remove(id);
    if (res?.status) load();
  };

  const showMembers = async (p) => {
    if (openId === p.id) {
      setOpenId(null);
      return;
    }
    const res = await student_package_list({ package_id: p.id });
    setMembers(Array.isArray(res?.data) ? res.data : []);
    setOpenId(p.id);
  };

  const statusPill = (p) => {
    const expired =
      p.sale_ends_on && new Date(p.sale_ends_on) < new Date(new Date().toDateString());
    if (p.status !== 1) return <span style={{ ...s.pill, ...s.off }}>OFF</span>;
    if (expired) return <span style={{ ...s.pill, ...s.expired }}>CLOSED</span>;
    return <span style={{ ...s.pill, ...s.on }}>ON SALE</span>;
  };

  return (
    <div className="right-nav" id="dash-event">
      <div className="modal-content">
        <div className="modal-header">
          <div className="col-md-6">
            <h4 className="modal-title">Packages</h4>
          </div>
        </div>

        <div className="modal-body">
          <p style={s.intro}>
            A package is one offer sold at a fixed price for a limited window.
            The price covers only the sessions listed in it &mdash; a condition
            such as &ldquo;must also enrol in WRMT&rdquo; is recorded as a
            condition, and that course is still charged separately at its
            normal rate.
          </p>

          <div style={s.panel}>
            <p style={s.panelTitle}>Current packages</p>
            {rows.length === 0 ? (
              <div style={s.empty}>No packages yet. Create one below.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Package</th>
                    <th style={s.th}>Price</th>
                    <th style={s.th}>Normally</th>
                    <th style={s.th}>Book fee</th>
                    <th style={s.th}>On sale until</th>
                    <th style={s.th}>Condition</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <React.Fragment key={p.id}>
                      <tr>
                        <td style={s.td}>
                          <strong>{p.name}</strong>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>
                            <code>{p.code}</code> &middot; {p.items?.length || 0} items
                          </div>
                        </td>
                        <td style={{ ...s.td, ...s.money }}>
                          {money(p.price)}
                          {p.saving > 0 && (
                            <div style={s.save}>save {money(p.saving)}</div>
                          )}
                        </td>
                        <td style={s.td}>{money(p.normal_price)}</td>
                        <td style={s.td}>{money(p.book_fee)}</td>
                        <td style={s.td}>
                          {p.sale_ends_on ? String(p.sale_ends_on).slice(0, 10) : "—"}
                        </td>
                        <td style={{ ...s.td, maxWidth: 220, fontSize: 12 }}>
                          {p.requires_note || "—"}
                        </td>
                        <td style={s.td}>{statusPill(p)}</td>
                        <td style={s.td}>
                          <button type="button" style={s.btnLink} onClick={() => edit(p)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            style={{ ...s.btnLink, marginLeft: 10 }}
                            onClick={() => showMembers(p)}
                          >
                            {openId === p.id ? "Hide students" : "Students"}
                          </button>
                          <button
                            type="button"
                            style={{ ...s.btnLink, color: "#6b7280", marginLeft: 10 }}
                            onClick={() => remove(p.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>

                      {openId === p.id && (
                        <tr>
                          <td style={{ ...s.td, background: "#f9fafb" }} colSpan={8}>
                            {members.length === 0 ? (
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                No students on this package yet.
                              </span>
                            ) : (
                              <table style={s.table}>
                                <thead>
                                  <tr>
                                    <th style={s.th}>Student</th>
                                    <th style={s.th}>User name</th>
                                    <th style={s.th}>Enrolled</th>
                                    <th style={s.th}>Paid price</th>
                                    <th style={s.th}>Sessions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {members.map((m) => (
                                    <tr key={m.id}>
                                      <td style={s.td}>
                                        {m.first_name} {m.last_name}
                                      </td>
                                      <td style={s.td}>{m.user_name || "—"}</td>
                                      <td style={s.td}>
                                        {String(m.enrolled_on).slice(0, 10)}
                                      </td>
                                      <td style={s.td}>{money(m.package_price)}</td>
                                      <td style={s.td}>{m.items?.length || 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={s.panel}>
            <p style={s.panelTitle}>
              {form.id ? `Edit “${form.name}”` : "Create a package"}
            </p>

            <div style={s.grid}>
              <div style={{ ...s.field, gridColumn: "span 2" }}>
                <label style={s.label}>Name *</label>
                <input
                  style={s.input}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Selective Trial Test Course — Special Offer 1"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Code *</label>
                <input
                  style={s.input}
                  value={form.code}
                  onChange={set("code")}
                  placeholder="stt-2026t3-offer1"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Package price *</label>
                <input
                  type="number" step="0.01" style={s.input}
                  value={form.price} onChange={set("price")} placeholder="3520"
                />
              </div>
              <div style={s.field}>
                <label style={s.label}>Normal price</label>
                <input
                  type="number" step="0.01" style={s.input}
                  value={form.normal_price} onChange={set("normal_price")}
                  placeholder={String(itemTotal || 4020)}
                />
                <span style={s.hint}>Blank uses the item total</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>Book fee</label>
                <input
                  type="number" step="0.01" style={s.input}
                  value={form.book_fee} onChange={set("book_fee")}
                  placeholder={String(bookTotal || 60)}
                />
                <span style={s.hint}>Charged on top, never discounted</span>
              </div>
              <div style={s.field}>
                <label style={s.label}>On sale until</label>
                <input
                  type="date" style={s.input}
                  value={form.sale_ends_on} onChange={set("sale_ends_on")}
                />
              </div>
              <div style={{ ...s.field, gridColumn: "1 / -1" }}>
                <label style={s.label}>Condition</label>
                <input
                  style={s.input}
                  value={form.requires_note}
                  onChange={set("requires_note")}
                  placeholder="Must also enrol in WRMT continuously — WRMT fee is separate"
                />
              </div>
              <div style={{ ...s.field, gridColumn: "1 / -1" }}>
                <label style={s.label}>Terms &amp; conditions</label>
                <textarea
                  style={s.textarea}
                  value={form.conditions}
                  onChange={set("conditions")}
                  placeholder="Payment due dates strictly applied. Discount forfeited if…"
                />
              </div>
            </div>

            <p style={{ ...s.panelTitle, marginTop: 20 }}>What the package covers</p>
            <div style={s.itemHead}>
              <span>Term</span>
              <span>Course</span>
              <span>Weeks</span>
              <span>Course fee</span>
              <span>Book fee</span>
              <span />
            </div>
            {form.items.map((it, i) => (
              <div key={i} style={s.itemRow}>
                <input
                  style={s.small} value={it.term_label}
                  onChange={setItem(i, "term_label")} placeholder="2026 Term 3"
                />
                <input
                  style={s.small} value={it.course_name}
                  onChange={setItem(i, "course_name")} placeholder="STTC"
                />
                <input
                  type="number" style={s.small} value={it.weeks}
                  onChange={setItem(i, "weeks")} placeholder="10"
                />
                <input
                  type="number" step="0.01" style={s.small} value={it.course_fee}
                  onChange={setItem(i, "course_fee")} placeholder="780"
                />
                <input
                  type="number" step="0.01" style={s.small} value={it.book_fee}
                  onChange={setItem(i, "book_fee")} placeholder="20"
                />
                <button
                  type="button"
                  style={{ ...s.btnLink, color: "#9ca3af" }}
                  onClick={() => dropItem(i)}
                >
                  &times;
                </button>
              </div>
            ))}
            <button type="button" style={s.btnGhost} onClick={addItem}>
              + Add a row
            </button>

            <div style={s.totals}>
              Items add up to <strong>{money(itemTotal)}</strong> plus{" "}
              <strong>{money(bookTotal)}</strong> in book fees.
              {form.price !== "" && (
                <>
                  <br />
                  Package price <strong>{money(form.price)}</strong>
                  {saving_ > 0 && (
                    <>
                      {" "}
                      &mdash; saves <strong>{money(saving_)}</strong> against{" "}
                      {money(form.normal_price)}
                    </>
                  )}
                </>
              )}
            </div>

            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <button type="button" style={s.btn} disabled={saving} onClick={save}>
                {saving ? "Saving…" : form.id ? "Save changes" : "Create package"}
              </button>
              <button type="button" style={s.btnGhost} onClick={() => setForm(blank)}>
                Clear
              </button>
            </div>

            <div style={s.note}>
              When a student is put on a package, the rows above are copied onto
              their record. Editing the package later will not change what
              somebody has already bought.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;
